import type { AnyFieldApi } from '@tanstack/react-form';
import type { UseAppForm } from '../../../form';

import { useMemo } from 'react';

import { useFieldContext, useFormContext } from '../../../form';
import RuleCheckbox from './RuleCheckbox';

interface FieldRuleCheckboxProps {
  /**
   * Permission rule name
   */
  ruleName: string;
  /**
   * Content type property name
   */
  propertyKey: string;
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Component test ID
   */
  testId?: string;
}

export type FieldRulePermissions = {
  canCreate?: string[];
  canRead?: string[];
  canUpdate?: string[];
  canDelete?: string[];
};

const FieldRuleCheckbox = ({
  ruleName,
  propertyKey,
  disabled = false,
  testId,
}: FieldRuleCheckboxProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<FieldRulePermissions>();

  const attributesValue = useMemo(() => field.state.value, [field.state.value]);

  const listeners = useMemo(
    () =>
      ['canCreate', 'canUpdate'].indexOf(ruleName) > -1
        ? {
            onChange: ({ value }: { value: string[] }) => {
              if (value) {
                form.setFieldValue(
                  `${field.name}.canRead`,
                  Array.from(
                    new Set([...(attributesValue?.canRead || []), ...value]),
                  ),
                );
              }
            },
          }
        : {},
    [attributesValue?.canRead, field.name, form, ruleName],
  );

  return (
    <form.Field
      name={`${field.name}.${ruleName}`}
      mode="array"
      listeners={listeners}
    >
      {(subField: AnyFieldApi) => (
        <RuleCheckbox
          field={subField}
          propertyKey={propertyKey}
          disabled={
            disabled ||
            (ruleName === 'canRead' &&
              (attributesValue?.canCreate?.includes(propertyKey) ||
                attributesValue?.canUpdate?.includes(propertyKey)))
          }
          testId={testId}
        />
      )}
    </form.Field>
  );
};

export default FieldRuleCheckbox;
